<template>
  <div class="collection-search">
    <v-row
      no-gutters
      class="mb-2">
      <v-col
        cols="6"
        class="pr-1">
        <v-select
          v-model="filterYear"
          :items="yearOptions"
          label="Year"
          outlined
          dense
          hide-details
          clearable />
      </v-col>
      <v-col
        cols="6"
        class="pl-1">
        <v-select
          v-model="filterMonth"
          :items="monthOptions"
          label="Month"
          outlined
          dense
          hide-details
          clearable />
      </v-col>
    </v-row>
    <search-box
      v-model="search"
      class="mb-4" />
    <v-row
      no-gutters
      class="mb-2">
      <v-col
        cols="6"
        class="pr-1">
        <v-autocomplete
          v-model="filterDesigner"
          :items="designerOptions"
          :menu-props="{ offsetY: true }"
          label="Designer"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details
          clearable />
      </v-col>
      <v-col
        cols="6"
        class="pl-1">
        <v-autocomplete
          v-model="filterMerchandiser"
          :items="merchandiserOptions"
          :menu-props="{ offsetY: true }"
          label="Merchandiser"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details
          clearable />
      </v-col>
    </v-row>
    <v-list
      two-line
      style="overflow-y: auto; height: 70vh"
      @scroll="onScroll">
      <v-list-item
        v-for="item in collections"
        :key="item.id"
        :class="{ 'active-item': selected === item.id }"
        @click="onSelect(item)">
        <span
          class="collection-avatar"
          :style="avatarStyleOf(item.name)">{{ initialsOf(item.name) }}</span>
        <v-list-item-content>
          <v-list-item-title>{{ item.name }}</v-list-item-title>
          <v-list-item-subtitle v-if="followDeviceTimezone">
            Sale Date {{ item.salesDate | salesDateInLocalTimeZone }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-else>
            Sale Date {{ item.salesDate | dateUserFormat }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <div
        v-if="loadingCollection"
        class="pa-4 text-center">
        <v-progress-circular
          indeterminate
          size="24" />
      </div>
      <div
        v-if="!loadingCollection && collections.length === 0"
        class="pa-4 text-center">
        No results
      </div>
    </v-list>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'
import PrototypeAttributeProvider from '@/resources/PrototypeAttributeProvider'
import SearchBox from '@/components/SearchBox.vue'

const ProductCollectionService = new ProductCollectionProvider()
const PrototypeAttributeService = new PrototypeAttributeProvider()

export default {
  components: {
    SearchBox
  },
  props: {
    followDeviceTimezone: {
      type: Boolean,
      default: false
    },
    filterForProductionPlanV3: {
      type: Boolean,
      default: false
    },
    preselectedId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      selected: null,
      loadingCollection: false,
      totalItems: 0,
      totalPages: 0,
      collections: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['salesDate'],
        sortDesc: [false]
      },
      search: '',
      filterYear: null,
      filterMonth: null,
      filterDesigner: null,
      filterMerchandiser: null,
      designerOptions: [],
      merchandiserOptions: []
    }
  },
  computed: {
    yearOptions () {
      const current = new Date().getFullYear() + 1
      const years = []
      for (let y = 2026; y <= current; y++) {
        years.push(y)
      }
      return years
    },
    monthOptions () {
      return [
        { text: 'January', value: 1 },
        { text: 'February', value: 2 },
        { text: 'March', value: 3 },
        { text: 'April', value: 4 },
        { text: 'May', value: 5 },
        { text: 'June', value: 6 },
        { text: 'July', value: 7 },
        { text: 'August', value: 8 },
        { text: 'September', value: 9 },
        { text: 'October', value: 10 },
        { text: 'November', value: 11 },
        { text: 'December', value: 12 }
      ]
    }
  },
  watch: {
    search () {
      clearTimeout(this.delaySearch)
      this.delaySearch = setTimeout(() => {
        this.getCollection(false, true)
      }, 300)
    },
    filterYear () {
      this.getCollection(false, true)
    },
    filterMonth () {
      this.getCollection(false, true)
    },
    filterDesigner () {
      this.getCollection(false, true)
    },
    filterMerchandiser () {
      this.getCollection(false, true)
    }
  },
  async mounted () {
    this.initSelectOptions()
    await this.getCollection()
    if (this.preselectedId) {
      await this.loadPreselectedCollection()
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),

    async getCollection (append = false, reset = false) {
      if (this.loadingCollection) return
      try {
        if (reset) {
          this.options.page = 1
          this.totalPages = 0
        }

        this.loadingCollection = true
        const sortBy = this.options.sortBy[0]
        const sortOrder = this.options.sortDesc[0] ? 'desc' : 'asc'

        const { data } = await ProductCollectionService.getAll({
          ...this.options,
          sortBy,
          sortOrder,
          search: this.search,
          isShowInProductionPlanV3: this.filterForProductionPlanV3,
          year: this.filterYear ?? undefined,
          month: this.filterMonth ?? undefined,
          designer: this.filterDesigner ?? undefined,
          merchandiser: this.filterMerchandiser ?? undefined
        })

        this.totalItems = data.total
        this.totalPages = data.pages

        if (append) {
          this.collections.push(...data.results)
        } else {
          this.collections = data.results
        }
      } catch (error) {
        this.setErrorPage(error.code)
      } finally {
        this.loadingCollection = false
      }
    },
    onScroll (e) {
      const el = e.target
      const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20

      if (bottom && !this.loadingCollection && this.options.page < this.totalPages) {
        this.options.page++
        this.getCollection(true)
      }
    },
    async initSelectOptions () {
      try {
        const { data } = await PrototypeAttributeService.getSquadMember({
          page: 1,
          itemsPerPage: 99999,
          sortBy: ['id'],
          sortDesc: [true]
        })

        this.designerOptions = data.results
          .filter((item) => item.job === 'designer')
          .map((item) => ({ id: item.id, name: item.name }))

        this.merchandiserOptions = data.results
          .filter((item) => item.job === 'merchandiser')
          .map((item) => ({ id: item.id, name: item.name }))
      } catch (error) {
        console.error('initSelectOptions', error)
        this.setSnackbar({
          value: true,
          message: error.message,
          type: 'error'
        })
      }
    },
    onSelect (item) {
      this.selected = item.id
      this.$emit('onSelect', item)
    },
    initialsOf (name) {
      const letters = (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase())
      return letters.join('')
    },
    avatarStyleOf (name) {
      const palette = [
        { bg: '#e9f1f3', color: '#155e74' },
        { bg: '#e8f5e9', color: '#2e7d32' },
        { bg: '#fce4ec', color: '#ad1457' },
        { bg: '#fff3e0', color: '#ef6c00' },
        { bg: '#ede7f6', color: '#5e35b1' },
        { bg: '#fffde7', color: '#9e8b00' }
      ]
      const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      const { bg, color } = palette[hash % palette.length]
      return { background: bg, color }
    },

    async loadPreselectedCollection () {
      try {
        const { data: item } = await ProductCollectionService.getById(this.preselectedId)

        if (!item?.id) {
          this.setSnackbar({
            value: true,
            message: 'ไม่พบ Collection ตาม URL ที่ระบุ',
            type: 'error'
          })
          return
        }

        if (!this.collections.some((c) => c.id === item.id)) {
          this.collections.unshift(item)
        }
        this.onSelect(item)
      } catch (error) {
        console.error('loadPreselectedCollection', error)
        this.setSnackbar({
          value: true,
          message: 'ไม่สามารถโหลด Collection ตาม URL ที่ระบุได้',
          type: 'error'
        })
      }
    }
  }
}
</script>
<style>
  .active-item {
    background-color: rgba(0, 0, 0, 0.08) !important;
  }

  .collection-search .v-list-item {
    align-items: center;
  }

  .collection-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    margin-right: 12px;
    background: #e9f1f3;
    color: #155e74;
  }
</style>
